/**
 * PurchaseTargetConfigModal
 * Configura a meta de compra da Compra Inteligente (N × estoque mínimo).
 */

import React, { useEffect, useState } from 'react'
import { X, Settings2, Loader2, AlertCircle } from 'lucide-react'
import { minimumStockService } from '../../../../services/purchasesApi'
import '../MinimumStockConfigModal/MinimumStockConfigModal.css'

const MULTIPLIER_OPTIONS = [1, 1.5, 2, 2.5, 3, 4, 5]

function PurchaseTargetConfigModal({ isOpen, onClose, onSaved }) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [purchaseTargetMultiplier, setPurchaseTargetMultiplier] = useState(1)

  useEffect(() => {
    if (!isOpen) return

    const loadConfig = async () => {
      setLoading(true)
      setError('')
      try {
        const response = await minimumStockService.getConfig()
        if (response.success && response.data) {
          setPurchaseTargetMultiplier(response.data.purchaseTargetMultiplier ?? 1)
        } else {
          setError(response.message || 'Não foi possível carregar a configuração')
        }
      } catch (err) {
        console.error('Erro ao carregar meta de compra:', err)
        setError(err.response?.data?.message || err.message || 'Erro ao carregar configuração')
      } finally {
        setLoading(false)
      }
    }

    loadConfig()
  }, [isOpen])

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      const response = await minimumStockService.updateConfig({ purchaseTargetMultiplier })
      if (response.success) {
        onSaved?.(response.data)
        onClose?.()
        return
      }
      setError(response.message || 'Não foi possível salvar a configuração')
    } catch (err) {
      console.error('Erro ao salvar meta de compra:', err)
      setError(err.response?.data?.message || err.message || 'Erro ao salvar configuração')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="minimum-stock-config-overlay" onClick={onClose}>
      <div className="minimum-stock-config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="minimum-stock-config-header">
          <div className="minimum-stock-config-header-left">
            <div className="minimum-stock-config-icon">
              <Settings2 size={22} />
            </div>
            <div>
              <h3>Meta de compra</h3>
              <p>Define quanto comprar em relação ao estoque mínimo</p>
            </div>
          </div>
          <button type="button" className="minimum-stock-config-close" onClick={onClose} aria-label="Fechar">
            <X size={22} />
          </button>
        </div>

        {loading ? (
          <div className="minimum-stock-config-loading">
            <Loader2 size={24} className="spinning" />
            <span>Carregando configurações...</span>
          </div>
        ) : (
          <div className="minimum-stock-config-body">
            {error ? (
              <div className="minimum-stock-config-error" role="alert">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            ) : null}

            <div className="minimum-stock-config-field">
              <label htmlFor="purchase-target-multiplier">Meta de compra (inteligência)</label>
              <p className="minimum-stock-config-hint">
                Quantidade sugerida na Compra Inteligente: N × estoque mínimo.
                Ex.: 2× compra até o dobro do mínimo cadastrado.
              </p>
              <select
                id="purchase-target-multiplier"
                value={purchaseTargetMultiplier}
                onChange={(e) => setPurchaseTargetMultiplier(Number(e.target.value))}
              >
                {MULTIPLIER_OPTIONS.map((factor) => (
                  <option key={factor} value={factor}>
                    {factor}× estoque mínimo
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="minimum-stock-config-footer">
          <button type="button" className="minimum-stock-config-btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="minimum-stock-config-btn-primary"
            onClick={handleSave}
            disabled={loading || saving}
          >
            {saving ? <Loader2 size={16} className="spinning" /> : null}
            Salvar configuração
          </button>
        </div>
      </div>
    </div>
  )
}

export default PurchaseTargetConfigModal
